import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  SHARED_CALCULATOR_EXAMPLES,
  buildLoadedProfileHistoryLabel,
  useSharedProfileHistory,
  type SharedCalculatorActiveSelection,
  type SharedCalculatorHistoryEntry,
  type SharedCalculatorProfile,
} from '../hooks/useSharedProfileHistory';
import { useProfile } from './ProfileContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LinkedCalculatorKey = 'buying' | 'selling' | 'maximizer' | 'decoupling';

/** Per-borrower fields carried between calculators. `name`, `cash` and `cpf` are
 *  optional so a calculator that doesn't track them can stay silent (the receiver
 *  keeps its own values). */
export interface SharedBorrowerFields {
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
  name?: string;
  cash?: number;
  cpf?: number;
}

/** Save/load feedback is a success by default; failures must say so. */
export type HistoryToastSeverity = 'success' | 'error';

export interface SharedBorrowerData {
  primary: SharedBorrowerFields;
  additional: SharedBorrowerFields;
  includeAdditional: boolean;
}

/** One seller's share of a sale. Cash is their slice of the proceeds after their own
 *  CPF refund; the refund goes back to their own CPF OA. */
export interface SharedProceedsSeller {
  name?: string;
  cashProceeds: number;
  cpfRefund: number;
}

// Data transferred from Selling → Buying on navigation:
//   sellers          → matched by name against Buying's borrowers; only the ones who
//                      are also buying feed the sale-proceeds and Additional CPF rows
//   netSalesProceeds → the whole sale's cash proceeds (every seller)
//   cpfWithdrawal    → the whole sale's CPF refund (every seller)
//   soldHdb          → true when the seller was on the HDB tab; lets Buying default
//                      the EC Resale Levy (payable when buying a 2nd subsidised flat).
export interface SharedProceedsData {
  netSalesProceeds: number;
  cpfWithdrawal: number;
  soldHdb: boolean;
  /** Per-seller split. Older senders omit it — treat the totals as one seller. */
  sellers?: SharedProceedsSeller[];
}

/** Extra tags stored in the history entry when saving (profile linkage). */
export interface SaveHistoryMeta {
  /** Calculator hosting the Save to History click. */
  savedFromCalculator?: LinkedCalculatorKey;
  /** Profile/group loaded at save time — Profile Manager offers to reload it. */
  savedProfileRef?: { type: 'profile' | 'group'; id: number };
}

export interface LinkedCalculatorContextValue {
  // Core history data (single shared instance)
  historyEntries: SharedCalculatorHistoryEntry[];
  historyLoading: boolean;
  activeSelection: SharedCalculatorActiveSelection | null;
  setActiveSelection: (s: SharedCalculatorActiveSelection | null) => void;
  selectedExample: string;
  setSelectedExample: (v: string) => void;

  // History editing
  editingHistoryId: string | null;
  editingHistoryLabel: string;
  setEditingHistoryLabel: (v: string) => void;
  startEditingLabel: (id: string) => void;
  cancelEditing: () => void;
  updateHistoryLabel: (id: string, label: string) => Promise<boolean>;
  deleteHistory: (id: string) => Promise<boolean>;
  formatTimestamp: (date: Date) => string;

  // Save to history — collects state from every linked calculator
  saveAllToHistory: (customLabel?: string, meta?: SaveHistoryMeta) => Promise<SharedCalculatorHistoryEntry | null>;

  // Why the last save resolved to `null` — for reporting the failure to the agent
  getLastHistoryError: () => string | null;

  // Load from history — distributes state to every linked calculator
  loadAllFromHistory: (historyId: string) => SharedCalculatorHistoryEntry | null;

  // Registration: each calculator registers its state getter/setter
  registerStateGetter: (key: LinkedCalculatorKey, getter: () => unknown) => void;
  registerStateSetter: (key: LinkedCalculatorKey, setter: (state: unknown) => void) => void;
  registerProfileBuilder: (key: LinkedCalculatorKey, builder: () => SharedCalculatorProfile[]) => void;

  // Toast
  showToast: (message: string, severity?: HistoryToastSeverity) => void;
  historyToast: { open: boolean; message: string; severity: HistoryToastSeverity };
  clearToast: () => void;

  // Profile name getter (from profile context)
  getSelectedName: (() => string | null | undefined) | null;
  setGetSelectedName: (fn: () => string | null | undefined) => void;

  // Borrower sync on navigation
  registerBorrowerExtractor: (key: LinkedCalculatorKey, extractor: () => SharedBorrowerData) => void;
  registerBorrowerApplier: (key: LinkedCalculatorKey, applier: (data: SharedBorrowerData) => void) => void;
  notifyActiveCalculator: (routeKey: string) => void;

  // Proceeds sync (Selling → Buying on navigation)
  registerProceedsExtractor: (key: LinkedCalculatorKey, extractor: () => SharedProceedsData) => void;
  registerProceedsApplier: (key: LinkedCalculatorKey, applier: (data: SharedProceedsData) => void) => void;
}

const CLEAR_SELECTION_VALUE = '__clear-selection__';
export { CLEAR_SELECTION_VALUE };

// ─── Pending cross-page history load ─────────────────────────────────────────
// Profile Manager's "Buying Calc" / "Maximizer" buttons live outside the
// LinkedCalculatorProvider, so they can't apply a history entry directly.
// They store the target entry id + fire an event; the provider consumes it once
// the history list is available (and after profile-selection effects, so the
// saved snapshot wins over the profile's default form population).
const PENDING_HISTORY_LOAD_KEY = 'superCalculatorPendingHistoryLoad';
const PENDING_HISTORY_LOAD_EVENT = 'super-calculator-pending-history-load';

/** Queue a history entry to be loaded by the LinkedCalculatorProvider (now if mounted, else on next mount). */
export function requestPendingHistoryLoad(historyId: string): void {
  try {
    localStorage.setItem(PENDING_HISTORY_LOAD_KEY, JSON.stringify({ historyId }));
  } catch (error) {
    console.error('Failed to store pending history load:', error);
    return;
  }
  window.dispatchEvent(new Event(PENDING_HISTORY_LOAD_EVENT));
}

/** True while a queued history load has not been consumed yet. Calculators' profile-load
 *  effects check this and skip their default form population (funds, borrowers, …) so the
 *  restored snapshot is never overwritten, regardless of effect ordering. */
export function hasPendingHistoryLoad(): boolean {
  try {
    return localStorage.getItem(PENDING_HISTORY_LOAD_KEY) !== null;
  } catch {
    return false;
  }
}

// ─── Profile save-back suppression ────────────────────────────────────────────
// "Save to History and Profile" writes the calculator inputs back to the loaded
// profile. updateProfile() then refreshes selectedProfile (new object identity),
// which would re-run the calculators' profile-load effects and clobber form state
// the profile doesn't hold (purchase-price lock, a removed borrower, the active
// history selection). Those effects skip while this flag is set.
let profileSaveBackInProgress = false;

/** Mark the start of a calculator → profile save-back. */
export function beginProfileSaveBack(): void {
  profileSaveBackInProgress = true;
}

/** End a save-back. Deferred a tick so the profile-load effects triggered by the
 *  final updateProfile() commit still observe the flag. */
export function endProfileSaveBack(): void {
  window.setTimeout(() => {
    profileSaveBackInProgress = false;
  }, 250);
}

/** True while a calculator is writing its inputs back to the loaded profile. */
export function isProfileSaveBackInProgress(): boolean {
  return profileSaveBackInProgress;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const LinkedCalculatorContext = createContext<LinkedCalculatorContextValue | null>(null);

export function useLinkedCalculator(): LinkedCalculatorContextValue {
  const ctx = useContext(LinkedCalculatorContext);
  if (!ctx) throw new Error('useLinkedCalculator must be used within a LinkedCalculatorProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

function routeKeyToCalcKey(routeKey: string): LinkedCalculatorKey | null {
  if (routeKey === 'buying-calculator') return 'buying';
  if (routeKey === 'selling-calculator') return 'selling';
  if (routeKey === 'affordability') return 'maximizer';
  if (routeKey === 'decoupling-calculator') return 'decoupling';
  return null;
}

export function LinkedCalculatorProvider({ children }: { children: React.ReactNode }) {
  const {
    historyEntries,
    historyLoading,
    activeSelection,
    editingHistoryId,
    editingHistoryLabel,
    setEditingHistoryLabel,
    saveToHistory,
    getLastHistoryError,
    loadFromHistory,
    setActiveSelection,
    updateHistoryLabel,
    startEditingLabel,
    cancelEditing,
    deleteHistory,
    formatTimestamp,
  } = useSharedProfileHistory();

  const { selectedProfile } = useProfile();

  const [selectedExample, setSelectedExample] = useState('');
  const [historyToast, setHistoryToast] = useState<{
    open: boolean;
    message: string;
    severity: HistoryToastSeverity;
  }>({ open: false, message: '', severity: 'success' });

  // Refs for registered getters/setters from each calculator
  const stateGetters = useRef<Partial<Record<LinkedCalculatorKey, () => unknown>>>({});
  const stateSetters = useRef<Partial<Record<LinkedCalculatorKey, (state: unknown) => void>>>({});
  const profileBuilders = useRef<Partial<Record<LinkedCalculatorKey, () => SharedCalculatorProfile[]>>>({});
  const getSelectedNameRef = useRef<(() => string | null | undefined) | null>(null);

  // Borrower sync refs
  const borrowerExtractors = useRef<Partial<Record<LinkedCalculatorKey, () => SharedBorrowerData>>>({});
  const borrowerAppliers = useRef<Partial<Record<LinkedCalculatorKey, (data: SharedBorrowerData) => void>>>({});
  const prevCalcKeyRef = useRef<LinkedCalculatorKey | null>(null);
  const pendingBorrowerSync = useRef<{ calcKey: LinkedCalculatorKey; data: SharedBorrowerData } | null>(null);

  // Proceeds sync refs (Selling → Buying)
  const proceedsExtractors = useRef<Partial<Record<LinkedCalculatorKey, () => SharedProceedsData>>>({});
  const proceedsAppliers = useRef<Partial<Record<LinkedCalculatorKey, (data: SharedProceedsData) => void>>>({});
  const pendingProceedsSync = useRef<{ calcKey: LinkedCalculatorKey; data: SharedProceedsData } | null>(null);

  const registerStateGetter = useCallback((key: LinkedCalculatorKey, getter: () => unknown) => {
    stateGetters.current[key] = getter;
  }, []);

  const registerStateSetter = useCallback((key: LinkedCalculatorKey, setter: (state: unknown) => void) => {
    stateSetters.current[key] = setter;
  }, []);

  const registerProfileBuilder = useCallback((key: LinkedCalculatorKey, builder: () => SharedCalculatorProfile[]) => {
    profileBuilders.current[key] = builder;
  }, []);

  const showToast = useCallback((message: string, severity: HistoryToastSeverity = 'success') => {
    setHistoryToast({ open: true, message, severity });
  }, []);

  const clearToast = useCallback(() => {
    setHistoryToast((t) => ({ ...t, open: false }));
  }, []);

  const setGetSelectedName = useCallback((fn: () => string | null | undefined) => {
    getSelectedNameRef.current = fn;
  }, []);

  const registerBorrowerExtractor = useCallback((key: LinkedCalculatorKey, extractor: () => SharedBorrowerData) => {
    borrowerExtractors.current[key] = extractor;
  }, []);

  const registerBorrowerApplier = useCallback((key: LinkedCalculatorKey, applier: (data: SharedBorrowerData) => void) => {
    borrowerAppliers.current[key] = applier;
    // Apply any pending sync data (destination calculator just mounted)
    if (pendingBorrowerSync.current?.calcKey === key) {
      applier(pendingBorrowerSync.current.data);
      pendingBorrowerSync.current = null;
    }
  }, []);

  const registerProceedsExtractor = useCallback((key: LinkedCalculatorKey, extractor: () => SharedProceedsData) => {
    proceedsExtractors.current[key] = extractor;
  }, []);

  const registerProceedsApplier = useCallback((key: LinkedCalculatorKey, applier: (data: SharedProceedsData) => void) => {
    proceedsAppliers.current[key] = applier;
    if (pendingProceedsSync.current?.calcKey === key) {
      applier(pendingProceedsSync.current.data);
      pendingProceedsSync.current = null;
    }
  }, []);

  // Called when the visible calculator changes — syncs borrower data,
  // and additionally proceeds data when navigating Selling → Buying.
  const notifyActiveCalculator = useCallback((routeKey: string) => {
    const calcKey = routeKeyToCalcKey(routeKey);
    if (!calcKey) return;
    const prev = prevCalcKeyRef.current;
    prevCalcKeyRef.current = calcKey;
    if (!prev || prev === calcKey) return;

    // Borrower sync (all directions)
    const borrowerExtractor = borrowerExtractors.current[prev];
    if (borrowerExtractor) {
      const data = borrowerExtractor();
      const applier = borrowerAppliers.current[calcKey];
      if (applier) {
        applier(data);
      } else {
        pendingBorrowerSync.current = { calcKey, data };
      }
    }

    // Proceeds sync (Selling or Decoupling → Buying).
    // A decoupling frees the exiting owner's cash and CPF for their next purchase, so it
    // hands Buying the same SharedProceedsData shape a sale does.
    if ((prev === 'selling' || prev === 'decoupling') && calcKey === 'buying') {
      const proceedsExtractor = proceedsExtractors.current[prev];
      if (proceedsExtractor) {
        const data = proceedsExtractor();
        const applier = proceedsAppliers.current.buying;
        if (applier) {
          applier(data);
        } else {
          pendingProceedsSync.current = { calcKey: 'buying', data };
        }
      }
    }
  }, []);

  // Combined save: collects state from all 3 calculators
  const saveAllToHistory = useCallback(
    async (customLabel?: string, meta?: SaveHistoryMeta): Promise<SharedCalculatorHistoryEntry | null> => {
      // Build shared profiles — first calculator with any, in this order
      let profiles: SharedCalculatorProfile[] = [];
      for (const key of ['maximizer', 'buying', 'selling', 'decoupling'] as LinkedCalculatorKey[]) {
        const builder = profileBuilders.current[key];
        if (builder) {
          const built = builder();
          if (built.length > 0) {
            profiles = built;
            break;
          }
        }
      }

      const buyingState = stateGetters.current.buying?.() ?? undefined;
      const sellingState = stateGetters.current.selling?.() ?? undefined;
      const maximizerState = stateGetters.current.maximizer?.() ?? undefined;
      const decouplingState = stateGetters.current.decoupling?.() ?? undefined;

      // Build the combined purchasePlannerState blob
      const purchasePlannerState: SharedCalculatorHistoryEntry['purchasePlannerState'] = {
        ...(maximizerState && typeof maximizerState === 'object' ? maximizerState as Record<string, unknown> : {}),
        buyingCalculatorState: buyingState,
        sellingCalculatorState: sellingState,
        decouplingCalculatorState: decouplingState,
        // Profile linkage tags — drive Profile Manager's per-card load buttons.
        ...(meta?.savedFromCalculator ? { savedFromCalculator: meta.savedFromCalculator } : {}),
        ...(meta?.savedProfileRef ? { savedProfileRef: meta.savedProfileRef } : {}),
      };

      const label = customLabel ?? buildLoadedProfileHistoryLabel(
        getSelectedNameRef.current?.() ?? null,
        activeSelection,
      );

      const created = await saveToHistory(
        { profiles, purchasePlannerState },
        label,
      );
      return created;
    },
    [activeSelection, saveToHistory],
  );

  // Combined load: distributes state to all 3 calculators
  const loadAllFromHistory = useCallback(
    (historyId: string): SharedCalculatorHistoryEntry | null => {
      const entry = loadFromHistory(historyId);
      if (!entry) return null;

      // Distribute buying calculator state
      const buyingState = entry.purchasePlannerState?.buyingCalculatorState;
      if (buyingState !== undefined) {
        stateSetters.current.buying?.(buyingState);
      }

      // Distribute selling calculator state
      const sellingState = entry.purchasePlannerState?.sellingCalculatorState;
      if (sellingState !== undefined) {
        stateSetters.current.selling?.(sellingState);
      }

      // Distribute decoupling calculator state
      const decouplingState = entry.purchasePlannerState?.decouplingCalculatorState;
      if (decouplingState !== undefined) {
        stateSetters.current.decoupling?.(decouplingState);
      }

      // Distribute maximizer state — its registered setter applies
      // purchasePlannerState.affordabilityState.inputs (toolbar loads on the
      // Maximizer face apply the same values again via onHistoryLoad; harmless).
      if (entry.purchasePlannerState) {
        stateSetters.current.maximizer?.(entry.purchasePlannerState);
      }

      return entry;
    },
    [loadFromHistory],
  );

  // ── Auto-restore saved inputs when a profile is loaded ─────────────────────
  // Loading a profile should bring back everything last saved with it — the full
  // input snapshot from the newest history entry tagged with this profile's
  // savedProfileRef — not just the borrower fields the profile itself holds.
  // Runs after the calculators' own profile-population effects (parent effects
  // run last in the commit), so the snapshot wins. Keyed by profile identity so
  // data-only refreshes (e.g. the save-back after "Save to History and Profile")
  // don't re-trigger it and clobber in-progress edits.
  const autoRestoredProfileKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProfile) {
      autoRestoredProfileKeyRef.current = null;
      return;
    }
    if (historyLoading) return;
    const key = `${selectedProfile.type}-${selectedProfile.id}`;
    if (autoRestoredProfileKeyRef.current === key) return;
    autoRestoredProfileKeyRef.current = key;
    // A Profile Manager button queued a specific snapshot — let that load win.
    // (This effect is declared before the consumer below, so the key is still set.)
    if (hasPendingHistoryLoad()) return;

    let newest: SharedCalculatorHistoryEntry | null = null;
    for (const entry of historyEntries) {
      const ref = entry.purchasePlannerState?.savedProfileRef;
      if (!ref || ref.type !== selectedProfile.type || ref.id !== selectedProfile.id) continue;
      if (!newest || entry.timestamp.getTime() > newest.timestamp.getTime()) newest = entry;
    }
    if (!newest) return;

    const entry = loadAllFromHistory(newest.id);
    if (!entry) return;
    setSelectedExample(newest.id);
    setActiveSelection({
      id: newest.id,
      source: 'history',
      profiles: entry.profiles,
      propertyType: entry.propertyType,
      purchasePlannerState: entry.purchasePlannerState,
    });
  }, [selectedProfile, historyLoading, historyEntries, loadAllFromHistory, setActiveSelection]);

  // ── Pending cross-page history load (see requestPendingHistoryLoad) ────────
  // Consumed via an effect so it runs AFTER descendant calculators' profile-load
  // effects in the same commit — the saved snapshot must win over the profile's
  // default form population.
  const [pendingLoadTick, setPendingLoadTick] = useState(0);
  useEffect(() => {
    const bump = () => setPendingLoadTick((t) => t + 1);
    window.addEventListener(PENDING_HISTORY_LOAD_EVENT, bump);
    return () => window.removeEventListener(PENDING_HISTORY_LOAD_EVENT, bump);
  }, []);

  useEffect(() => {
    if (historyLoading) return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PENDING_HISTORY_LOAD_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    // Consume the request whether or not the entry still exists — a stale key
    // must not fire on some later visit.
    try {
      localStorage.removeItem(PENDING_HISTORY_LOAD_KEY);
    } catch { /* ignore */ }
    let historyId: string | undefined;
    try {
      historyId = (JSON.parse(raw) as { historyId?: string }).historyId;
    } catch {
      historyId = undefined;
    }
    if (!historyId) return;
    const entry = loadAllFromHistory(historyId);
    if (!entry) return;
    setSelectedExample(historyId);
    setActiveSelection({
      id: historyId,
      source: 'history',
      profiles: entry.profiles,
      propertyType: entry.propertyType,
      purchasePlannerState: entry.purchasePlannerState,
    });
  }, [historyLoading, pendingLoadTick, loadAllFromHistory, setActiveSelection, setSelectedExample]);

  const value: LinkedCalculatorContextValue = {
    historyEntries,
    historyLoading,
    activeSelection,
    setActiveSelection,
    selectedExample,
    setSelectedExample,
    editingHistoryId,
    editingHistoryLabel,
    setEditingHistoryLabel,
    startEditingLabel,
    cancelEditing,
    updateHistoryLabel,
    deleteHistory,
    formatTimestamp,
    saveAllToHistory,
    getLastHistoryError,
    loadAllFromHistory,
    registerStateGetter,
    registerStateSetter,
    registerProfileBuilder,
    showToast,
    historyToast,
    clearToast,
    getSelectedName: getSelectedNameRef.current,
    setGetSelectedName,
    registerBorrowerExtractor,
    registerBorrowerApplier,
    notifyActiveCalculator,
    registerProceedsExtractor,
    registerProceedsApplier,
  };

  return (
    <LinkedCalculatorContext.Provider value={value}>
      {children}
    </LinkedCalculatorContext.Provider>
  );
}
